export const enAppStudio = {
  title: "App Studio",
  subtitle: "Build and manage your visualization apps.",
  empty: "No apps yet. Create your first app to get started.",
  actions: {
    newApp: "New App",
    importApp: "Import App",
    importAppFileAria: "App export file",
  },
  queryBuilder: {
    description:
      "Pick metrics, dimensions and an optional time range to generate a SQL query.",
    loading: "Loading available fields…",
    empty:
      "This app's data source doesn't have a semantic model yet, so the form builder isn't available. Use the Query tab to write SQL directly.",
    fields: {
      metrics: "Metrics & measures",
      dimensions: "Dimensions",
      timeRange: "Filter by time range",
      startDate: "Start date",
      endDate: "End date",
      grain: "Group by",
    },
    grainNone: "No time grouping",
    grainOptions: {
      day: "Day",
      week: "Week",
      month: "Month",
      quarter: "Quarter",
      year: "Year",
    },
    // The searchable metric / dimension pickers.
    picker: {
      search: "Search…",
      selected_one: "{{count}} selected",
      selected_other: "{{count}} selected",
      clear: "Clear",
      done: "Done",
      noMatches: "No matches",
    },
    generate: "Generate SQL",
    generating: "Generating…",
    generatedSqlLabel: "Generated SQL",
    errors: {
      loadFailed: "Could not load available fields. Please try again.",
      generateFailed: "Could not generate the query. Please try again.",
    },
  },
  // A breakdown re-cuts linked charts and tables (Day / Week / Month …) instead of filtering them.
  breakdowns: {
    title: "Breakdowns",
    addBreakdown: "Add breakdown",
    barAria: "Breakdowns for this tab",
    switcherAria: "{{name}}",
    notAvailable: "Not available yet",
    noLinkedWidgets:
      "No widget uses this breakdown yet. Link a chart or table from Edit widget › Widget settings › Breakdown.",
    updating: "Updating…",
    actionsAria: "Actions for {{name}}",
    edit: "Edit",
    delete: "Delete",
    // The Breakdown row of a saved chart or table in Edit widget › Widget settings.
    field: {
      label: "Breakdown",
      none: "Not set up",
      setUp: "Set up",
      edit: "Edit",
      setUpAria: "Set up a breakdown for {{name}}",
      editAria: "Edit the breakdown of {{name}}",
      placement: {
        tab: "Tab breakdown",
        widget: "This widget only",
      },
      notReady_one: "{{count}} view isn't ready — open the breakdown to retry it.",
      notReady_other: "{{count}} views aren't ready — open the breakdown to retry them.",
      inactive: "Not applied: the widget has moved off its breakdown's tab.",
      fileBacked: "File-backed widgets can't use a breakdown.",
      saveQueryFirst:
        "Save your query changes first — each option's view is written from the saved query.",
    },
    snapshotHint: "Captured as {{option}}",
    errors: {
      switchFailed: "Could not switch the breakdown. Please try again.",
      loadFailed: "Could not load this app's breakdowns.",
      nameRequired: "Give the breakdown a name.",
      nameTooLong: "Keep the name to {{max}} characters.",
      tooFewOptions: "Add at least {{min}} options.",
      tooManyOptions: "A breakdown can have up to {{max}} options.",
      optionLabelRequired: "Every option needs a label.",
      optionLabelTooLong: "Keep each option to {{max}} characters.",
      duplicateOptionLabel: "Each option needs a different label.",
      descriptionTooLong: "Keep the description to {{max}} characters.",
      saveFailed: "Could not save the breakdown. Please try again.",
      deleteFailed: "Could not delete the breakdown. Please try again.",
      tabBreakdownRequired: "Choose one of this tab's breakdowns.",
      queryRequired: "Pick a saved query for {{option}}.",
      queriesLoadFailed: "Could not load this app's saved queries.",
      regenerateFailed: "Could not regenerate that view. Please try again.",
    },
    config: {
      createTitle: "Add breakdown",
      editTitle: "Edit breakdown",
      closeAria: "Close",
      intro:
        "A breakdown re-cuts the charts and tables linked to it — by day, week or month, by region, or any other way — without filtering out any rows. Viewers switch between its options from this tab.",
      nameLabel: "Name",
      namePlaceholder: "Date breakdown",
      descriptionLabel: "How each option changes the data (optional)",
      descriptionHelper:
        "Helps the AI write each view, e.g. “Group the order date by the chosen period.”",
      cancel: "Cancel",
      create: "Add breakdown",
      save: "Save changes",
      saving: "Saving…",
      regenerating: "Saving and regenerating linked views… This can take a minute.",
      regenerateNotice:
        "Changing the options regenerates the views of every widget linked to this breakdown.",
    },
    options: {
      label: "Options",
      helper: "{{min}}–{{max}} options. The default is what viewers see first.",
      optionLabel: "Option {{index}}",
      defaultColumn: "Default",
      defaultAria: "Make {{option}} the default",
      dragAria: "Drag to reorder {{option}}",
      removeAria: "Remove {{option}}",
      add: "Add option",
      presetsLabel: "Quick start:",
      presets: {
        dateGrains: "Day · Week · Month · Quarter · Year",
      },
      presetValues: {
        day: "Day",
        week: "Week",
        month: "Month",
        quarter: "Quarter",
        year: "Year",
      },
    },
    deleteDialog: {
      heading: "Delete “{{name}}”?",
      subheading:
        "Widgets linked to it go back to showing only their own query, and viewers lose this switcher. This cannot be undone.",
      cancel: "Cancel",
      confirm: "Delete breakdown",
    },
    widget: {
      title: "Breakdown",
      closeAria: "Close",
      intro:
        "Let viewers switch how this widget breaks its data down. Each option gets its own view of the query — written by AI, or a saved query you pick.",
      sourceLabel: "Switch views with",
      tabSource: "A tab breakdown",
      tabSourceHint: "Switches together with the other widgets on this tab, from the breakdown bar.",
      tabSourceEmpty:
        "This tab has no breakdowns yet — add one from the breakdown bar above the widgets.",
      tabBreakdownLabel: "Tab breakdown",
      ownSource: "Only this widget",
      ownSourceHint: "Its switcher sits in this widget's header.",
      ownNameDefault: "Breakdown",
      tabLinkedNotice:
        "This widget switches with “{{name}}”, a breakdown shared across its tab. Its options are copied below — save to give the widget a breakdown of its own.",
      baseLabel: "This widget's query already shows",
      baseNone: "None of these",
      baseHelper: "That option runs your query as it is; every other option gets its own view.",
      viewsLabel: "Views",
      viewSource: {
        base: "Current query",
        generated: "Generate with AI",
        query: "Use a saved query",
      },
      viewSourceAria: "Where {{option}} gets its data",
      queryAria: "Saved query for {{option}}",
      queryPlaceholder: "Pick a query",
      status: {
        ready: "Ready",
        failed: "Failed",
        stale: "Out of date",
        missing: "Not generated",
        pending: "Generated on save",
      },
      staleHint: "The widget's query changed since this view was written.",
      retry: "Retry",
      regenerate: "Regenerate",
      generating_one: "Generating {{count}} view with AI… This can take a minute.",
      generating_other: "Generating {{count}} views with AI… This can take a minute.",
      saving: "Saving…",
      savedSummary: "{{ready}} of {{total}} views are ready.",
      savedAllReady: "Every view is ready.",
      cancel: "Cancel",
      close: "Close",
      save: "Save",
      remove: "Remove breakdown",
      inactive:
        "This widget has moved off its breakdown's tab, so the breakdown is not applied. Save to link it again.",
      removeDialog: {
        heading: "Remove the breakdown from this widget?",
        subheading: "The widget goes back to showing only its own query.",
        ownSubheading:
          "The widget goes back to showing only its own query, and its own breakdown is deleted.",
        cancel: "Cancel",
        confirm: "Remove",
      },
    },
  },
  tabFilters: {
    title: "Filters",
    addFilter: "Add filter",
    clearAll: "Clear all",
    apply: "Apply",
    done: "Done",
    anyValue: "Any",
    valuePlaceholder: "Select values",
    freeTextPlaceholder: "Type a value and press Enter",
    startDate: "Start",
    endDate: "End",
    dateMode: "Date range",
    dateModeCustom: "Custom",
    asOfDate: "As of",
    kindDateRange: "Date range",
    kindDateRangeHint: "Report on a period — widgets show data inside it",
    kindAsOf: "As of date",
    kindAsOfHint: "Pick one date — every window slides to end on it",
    datePresets: {
      last_calendar_week: "Last calendar week",
      last_calendar_month: "Last calendar month",
      last_7_days: "Last 7 days",
      last_2_weeks: "Last 2 weeks",
      last_1_month: "Last month",
    },
    notFilteredBy: "Not filtered by {{dimensions}}",
    // While a filter change is still preparing the tab's filters (can take tens of seconds).
    preparing: "Preparing filters for this tab…",
    preparingWidget: "Preparing filters…",
    preparingWidgetHint:
      "The tab's filters were just changed and are still being prepared for this widget. It refreshes once they are ready.",
    previewFilteredBy: "Filtered by this tab's {{dimensions}}",
    notFilteredFileSource: "Tab filters do not apply to file-based data",
    unappliedChanges: "Unapplied changes — click Apply to update the widgets",
    reorderFailed: "Could not save the new filter order",
    valuesSource: "Values from",
    valuesSourceOntology: "Ontology values",
    valuesSourceQuery: "Custom query",
    valuesSourceFreeText: "Typed values",
    valuesSourceDates: "Date picker",
    allChoice: "All",
    noneChoice: "No filter",
    allLabel: "Label for All",
    noneLabel: "Label for No filter",
    offerAll: "Offer “All”",
    offerNone: "Offer “No filter”",
    defaultMode: "When the app opens",
    defaultFirst: "First value",
    defaultFirstWithValue: "First value (now: {{value}})",
    defaultValues: "Specific values",
    defaultValuesLabel: "Default values — what the app opens with",
    workingSelectionHint: "Preview only — the default is set in Configure.",
    defaultFailed: "Could not save this filter's default.",
    selectionFailed: "Could not update this filter's settings.",
    selectionMode: "Selection",
    singleSelect: "Single value",
    multiSelect: "Multiple values",
    singleValuePlaceholder: "Select a value",
    emptyBehavior: "When nothing is selected",
    emptySkip: "Don't filter",
    emptyMatchNothing: "Show no data",
    onParentChange: "When {{parents}} changes",
    parentKeepOrFirst: "Keep if still available, else first value",
    parentFirst: "First value",
    parentAll: "All values",
    parentClear: "Clear the selection",
    cascadeFailed: "Could not update the filters that depend on this one.",
    displayName: "Filter name",
    displayNameDefault: "Default: {{name}}",
    resetDisplayName: "Reset to the dimension's name",
    renameFailed: "Could not rename this filter.",
    periodPlaceholder: "Select a period",
    valuesQueryConfigured: "Values come from a query that runs each time the app opens.",
    editValuesQuery: "Edit query",
    valuesQueryFailed: "The values query failed: {{error}}",
    valuesSourceFailed: "Could not change where this filter's values come from.",
    appLevel: {
      badge: "Applies to all tabs",
      badgeSelected: "Shown on {{tabs}}",
      badgeShared: "Shared with other tabs",
      scopeHeading: "Show this filter on",
      scopeThisTab: "This tab only",
      scopeAllTabs: "All tabs",
      scopeAllTabsHint: "Every tab, including tabs added later",
      scopeSelectedTabs: "Selected tabs",
      scopeSelectedTabsHint: "Only the tabs you pick",
      scopeHint: "A shared filter keeps one pick: choosing a value on one tab carries to the others.",
      tabListAria: "Tabs to show this filter on",
      currentTab: "{{name}} (this tab)",
      applySelection: "Apply",
      discardSelection: "Cancel",
      selectionNotApplied: "Not applied yet",
      pickAnotherTab: "Pick at least one other tab.",
      scopeFailed: "Could not change which tabs this filter applies to.",
      cancel: "Cancel",
      promoteHeading: "Apply “{{name}}” to all tabs?",
      promoteBody:
        "It will show on every tab, and replace any “{{name}}” filter other tabs already have.",
      promoteBodyWithParents:
        "It will show on every tab, and replace any “{{name}}” filter other tabs already have. Its values depend on {{parents}}, which will apply to all tabs too.",
      promoteConfirm: "Apply to all tabs",
      demoteHeading: "Make “{{name}}” this tab's only?",
      demoteBody: "It will be removed from every other tab.",
      demoteBodyWithDependents:
        "It will be removed from every other tab. {{dependents}} depend on it and will move to this tab too.",
      demoteConfirm: "Keep on this tab only",
      shareHeading: "Show “{{name}}” on selected tabs?",
      shareBody:
        "It will show on {{tabs}}, and replace any “{{name}}” filter those tabs already have.",
      shareBodyRemoved: "It will be removed from {{tabs}}.",
      shareBodyRelated: "{{names}} will move with it, since their values are nested with it.",
      shareConfirm: "Show on selected tabs",
      removeHeading: "Remove “{{name}}” from all tabs?",
      removeBody:
        "This filter applies to every tab of the app. Removing it here removes it from all of them.",
      removeConfirm: "Remove from all tabs",
      removeSelectedHeading: "Remove “{{name}}” from every tab it is on?",
      removeSelectedBody:
        "This filter is shown on {{tabs}}. Removing it here removes it from all of them.",
      removeSelectedConfirm: "Remove from these tabs",
      clearHeading: "Clear filters, including shared ones?",
      clearBody:
        "{{names}} are shared with other tabs. Clearing removes them from those tabs too, not just this one.",
      clearConfirm: "Clear all",
    },
  },
  // A widget on a comparison query: the periods it compares, dated for its viewers.
  comparisonPeriods: {
    range: "{{start}} – {{end}}",
    comparedWith: "Compared with {{range}}",
    comparedWithHint: "Shows {{current}}, compared with {{previous}}.",
    // A KPI change chip with no label of its own.
    kpiChipLabel: "vs {{range}}",
  },
  filterConfig: {
    open: "Configure filter",
    title: "Configure — {{name}}",
    closeAria: "Close",
    tabsAria: "Filter settings",
    tabs: {
      general: "General",
      values: "Values",
      default: "Default",
      behavior: "Behavior",
    },
    choicesHeading: "Extra choices for viewers",
    choicesHint: "Shown at the top of the filter's list. Leave a label empty to use the default wording.",
    emptyHint:
      "“Don't filter” leaves this filter out of the widgets' queries; “Show no data” applies it so nothing matches.",
    parentHint: "What this filter's selection becomes when a filter its values query reads changes.",
    savedAsYouGo: "Changes are saved as you make them.",
    saving: "Saving…",
    allSaved: "All changes saved",
    saveFailed: "Could not save this change. Please try again.",
    tabsNotApplied: "Your tab selection isn't applied yet.",
    applyAndClose: "Apply and close",
    done: "Done",
  },
  filterValuesQuery: {
    title: "Filter values — {{name}}",
    closeAria: "Close",
    description:
      "Write a query whose result becomes this filter's values. It runs every time the app is opened, so the list stays up to date.",
    queryHeading: "Query",
    columnsHint:
      "Each row becomes one choice. After running, pick the column people see (e.g. a title name) and the column the filter applies (e.g. the title code). With one column, both are the same.",
    columnsHintDateRange:
      "Each row becomes one named period. After running, pick the column people see (e.g. a season) and the columns holding its start and end dates — picking it filters to that window.",
    mappingHeading: "Columns",
    labelColumn: "Shown to the user",
    valueColumn: "Applied to {{name}}",
    startColumn: "Start date",
    endColumn: "End date",
    sample: "“{{label}}” → {{name}} = {{value}}",
    sampleDateRange: "“{{label}}” → {{name}} from {{start}} to {{end}}",
    rowCount: "Rows returned: {{count}}",
    otherFiltersHint:
      "To make this list follow another filter, add a placeholder in the WHERE clause. Click a filter to copy one, then replace “column” with the column it should match.",
    placeholderCopied: "Copied {{snippet}}",
    ranAs: "Ran as (with the other filters' current values)",
    sqlLabel: "SQL",
    resultsHeading: "Results",
    run: "Run",
    refresh: "Refresh",
    running: "Running query…",
    runFailed: "Could not run the query.",
    stale: "The query has changed — run it again before saving.",
    empty: "The query returned no rows, so the filter would have no values.",
    hint: "Run the query to see the values the filter will offer.",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    saveFailed: "Could not save the query.",
  },
  section: {
    toggleAriaLabel: "Toggle {{title}} section",
  },
  list: {
    ariaLabel: "Apps",
    unknownCategory: "Uncategorized",
    loadFailed: "Could not load apps. Please try again.",
  },
  card: {
    moreActionsAriaLabel: "More actions",
    delete: "Delete",
    share: "Share",
    duplicate: "Duplicate",
    export: "Export",
  },
  shareDialog: {
    heading: 'Share "{{name}}"',
    tabs: {
      aria: "Ways to share",
      people: "People",
      link: "Public link",
    },
    publishFirst: "Publish the app to create a public link.",
    access: {
      subheading: "Choose who can see this app. People still need access to its domain.",
      createdBy: "Created by {{name}}",
      audience: {
        private: "Only me",
        everyone: "Everyone with access to the domain",
        selected: "Selected users",
      },
      levels: {
        view: "Can view",
        manage: "Can manage",
      },
      levelHints: {
        view: "Sees the published app",
        manage: "Can also edit it in App Studio",
      },
      levelMenu: {
        userAria: "Access for {{name}}",
        newPeopleAria: "Access for the people you add",
        everyoneAria: "Access for everyone with access to the domain",
      },
      usersLabel: "Add people",
      usersHelper: "Only users with access to this app's domain are listed.",
      noUsers: "No users found",
      addUsers: "Add",
      peopleListLabel: "People with access",
      removeUser: "Remove {{name}}",
      removeTooltip: "Remove access",
      tabAccess: {
        buttonAria: "Tabs {{name}} can see",
        menuAria: "Choose the tabs {{name}} can see",
        newPeopleButtonAria: "Tabs the people you add can see",
        newPeopleMenuAria: "Choose the tabs the people you add can see",
        allTabs: "All tabs",
        allTabsHint: "Includes tabs added later",
        someTabs: "{{count}} of {{total}} tabs",
        noTabs: "No tabs",
        manageSeesAll: "People who can manage see every tab.",
        limitedNotice:
          "People limited to some tabs won't see tabs added later until you add them here.",
        everyoneNotice:
          "Everyone sees every tab. To limit the tabs someone sees, share with selected users instead.",
        noTabsError: "Choose at least one tab for {{name}}, or remove them.",
      },
      privateNotice: "This app can be viewed only by you unless you give access to others.",
      loading: "Loading access",
      loadFailed: "Could not load who this app is shared with. Please try again.",
      save: "Save",
      saveFailed: "Could not save access. Please try again.",
      saved: "Access updated",
    },
    subheading:
      "Anyone with the link can view this app without signing in. Choose when the link stops working.",
    oneDay: "1 day",
    sevenDays: "7 days",
    thirtyDays: "30 days",
    never: "Never expires",
    custom: "Custom date and time",
    customLabel: "Expires at",
    customHelper: "Interpreted in your current timezone.",
    linkLabel: "Shareable link",
    linkHelper: "Copy it now — this link is shown only once.",
    copyAria: "Copy to clipboard",
    copiedAria: "Copied",
    expiresOn: "Expires {{when}}",
    neverExpires: "This link never expires.",
    generate: "Generate link",
    cancel: "Cancel",
    done: "Done",
    // Shown only for apps that have been captured at least once — an app with no snapshots has
    // nothing to choose between.
    viewMode: {
      label: "What should this link show?",
      default: "Default only",
      defaultAndSnapshots: "Default + all snapshots",
      singleSnapshot: "One snapshot",
      allSnapshots: "All snapshots",
      snapshotLabel: "Snapshot to share",
    },
    errors: {
      invalidCustom: "Pick a date and time in the future.",
      failed: "Could not create the link. Please try again.",
      snapshotRequired: "Pick which snapshot this link should show.",
    },
  },
  deleteDialog: {
    heading: "Delete app",
    subheading:
      "This app and all of its charts and tables will be permanently deleted and cannot be recovered.",
    cancel: "Cancel",
    confirm: "Delete",
    error: "Delete failed. Please try again.",
  },
  deleteSuccess: "App deleted successfully.",
  exportFailed: "Could not export the app. Please try again.",
  importInvalidFile:
    "That file is not a valid app export. Choose a .json file exported from App Studio.",
  build: {
    header: "Build {{name}}",
    untitledApp: "New App",
    backAria: "Back to App Studio",
    loadingAria: "Loading app",
    notFound: "App not found.",
    fields: {
      name: "App Name",
      description: "Description",
    },
    validation: {
      nameRequired: "App name is required.",
    },
    errors: {
      loadFailed: "Could not load the app. Please try again.",
      updateFailed: "Could not save changes. Please try again.",
    },
    publish: {
      action: "Publish",
      unpublish: "Unpublish",
    },
    // A published app is edited through a draft version that viewers do not see until published.
    draft: {
      publishAction: "Publish changes",
      discardAction: "Discard draft",
      loadFailed: "Could not open the draft version of this app. Please try again.",
      publishFailed: "Could not publish the changes. Please try again.",
      discardFailed: "Could not discard the draft. Please try again.",
      discardDialog: {
        heading: "Discard draft changes?",
        subheading:
          "Every change made since the app was last published will be lost, and the builder will show the published version again. Viewers are not affected.",
        cancel: "Keep editing",
        confirm: "Discard draft",
      },
    },
    items: {
      empty:
        "Nothing here yet. Add charts or tables from chat results using Add to app.",
      emptyTab:
        "This tab has no widgets yet. Add a chart, table, or KPI card to it.",
      loadingAria: "Loading charts and tables",
      // Shown inside a single widget's shell while its own query is still running -- the grid
      // itself has already painted, so this is never the whole panel's loading state.
      widgetLoadingAria: "Loading this widget's data",
      widgetLoadFailed: "Could not run this widget's query.",
      dragHandleAria: "Reorder item",
      addAction: "Add",
      addMenu: {
        chart: "Chart",
        table: "Table",
        kpi: "KPI card",
        tab: "Tab",
        widgets: "Widgets",
        nuggetFeed: "Nugget Feed",
        text: "Text",
      },
      // "Details", not "drill-down": the published view (publishedApps.ts) has always used the
      // plain word with real end users, and the builder saying something else made one feature
      // read as two. The key path stays `drilldown` to match the API field and the
      // `app_widget_drilldowns` table -- jargon is fine in code, just not on screen.
      drilldown: {
        openAria: "Show details for {{name}}",
        // No "Details ·" prefix: the widget's own name is already the title, and the prefix only
        // pushed it out of a narrow dialog header. Matches publishedApps.filteredDialogTitle.
        dialogTitle: "{{name}}",
        filteredDialogTitle: "{{name}} · {{filter}}",
        close: "Close details",
        loading: "Loading details…",
        loadFailed: "Could not load these details. Please try again.",
        unlink: "Unlink",
        deleteChoice: {
          label: "Clicking this card opens another widget.",
          unlink: "Keep it and show it on the tab",
          delete: "Also delete that widget",
        },
        errors: {
          unlinkFailed: "Could not unlink that widget. Please try again.",
        },
      },
      errors: {
        loadFailed: "Could not load charts and tables. Please try again.",
        reorderFailed: "Could not save the new order. Please try again.",
      },
    },
    charts: {
      sectionTitle: "Charts",
      loadingAria: "Loading charts",
      empty: "No charts yet. Add charts from chat results using Add to app.",
      columnSpan: {
        label: "Width",
        option: "{{span}} of {{total}} columns",
        extraSmall: "Extra Small",
        small: "Small",
        medium: "Medium",
        large: "Large",
        extraLarge: "Extra Large",
        full: "Full",
      },
      errors: {
        loadFailed: "Could not load charts. Please try again.",
        updateFailed: "Could not save chart changes. Please try again.",
      },
    },
    tables: {
      drilldown: {
        cellAria: "Show details for {{value}}, filtered by {{filter}}",
        // Stands in for a filter column the clicked row left empty. Those rows are still
        // clickable -- the details view then shows the target's own empty rows -- so the title
        // needs a word for "nothing" rather than a gap the reader has to interpret.
        emptyValue: "(empty)",
      },
      sectionTitle: "Tables",
      loadingAria: "Loading tables",
      empty: "No tables yet. Add tables from chat results using Add to app.",
      expand: "Expand table",
      close: "Close expanded table",
      expandedFallbackTitle: "Table",
      sort: {
        clear: "Clear sort",
      },
      rotate: {
        // Heads a rotated column that has no heading of its own and no header column to take one from.
        defaultHeading: "Row {{number}}",
      },
      selection: {
        selectAllAria: "Select all rows",
        selectRowAria: "Select row {{index}}",
      },
      taxonomy: {
        cellPlaceholder: "Not set",
        addValue: 'Add "{{value}}"',
      },
      pagination: {
        pageLabel: "Page {{page}} of {{totalPages}}",
        rowRangeLabel: "Rows {{from}}–{{to}} of {{total}}",
        loadingAria: "Loading page",
      },
      errors: {
        loadFailed: "Could not load tables. Please try again.",
        updateFailed: "Could not save table changes. Please try again.",
        pageFailed: "Could not load that page. Please try again.",
      },
    },
    kpis: {
      loadingAria: "Loading KPI cards",
      empty: "No KPI cards yet.",
      errors: {
        loadFailed: "Could not load KPI cards. Please try again.",
        updateFailed: "Could not save KPI card changes. Please try again.",
      },
    },
    chat: {
      title: "App Builder",
      subtitle:
        "Build and arrange this app by asking. New widgets and changes to existing ones are previewed before anything is saved.",
      welcome:
        "Hi! I can build charts, tables, KPI cards, notes and nugget feeds — describe what you want, paste a query, or point me at one this app already has. I can also add, rename and reorder tabs, move widgets around, and restyle them. Use the @ button to tell me which widget you mean.",
      inputPlaceholder:
        "Describe a widget, paste a query, or rearrange the app…",
      send: "Send",
      working: "Working on it…",
      openFabAria: "Open app builder chat",
      closeAria: "Close app builder chat",
      newChat: "New chat",
      stopAria: "Stop generating",
      referenceAria: "Reference {{name}} in the app builder chat",
      referenceTooltip: "Reference in chat",
      referencesHeading_one: "Talking about 1 widget",
      referencesHeading_other: "Talking about {{count}} widgets",
      clearReferences: "Clear",
      proposal: {
        previewHeading: "Preview — not saved yet",
        pinnedRowsSample:
          "Pinned rows here are calculated over the preview's rows. In the app, they calculate over every row of the result.",
        confirmHeading: "Confirm",
        apply: "Apply",
        discard: "Discard",
        createAsNewWidget: "Create as a new widget",
        confirmDelete: "Delete",
        applied: "Applied",
        discarded: "Discarded",
        failed: "Could not apply",
        loadingAria: "Loading preview",
        previewUnavailable:
          "Preview unavailable — the change can still be applied.",
        sqlFromYou: "Query you provided",
        sqlFromAssistant: "Query written for you",
        sqlReused: "Existing query reused",
        sqlSavedOnApply: "This query is saved with the widget when you apply.",
        replacesWidget:
          "This will replace “{{title}}”: once you apply, that widget shows this result instead of its current data.",
        replacesWidgetUntitled:
          "This will replace an existing widget: once you apply, it shows this result instead of its current data.",
        rowCount_one: "1 row",
        rowCount_other: "{{count}} rows",
        removedNuggets_one: "Takes 1 nugget out of the feed:",
        removedNuggets_other: "Takes {{count}} nuggets out of the feed:",
        untitledNugget: "Untitled nugget",
        // Widgets on a new comparison query: described where a query's SQL would show.
        comparison: {
          heading: "Comparison",
          compares: "“{{base}}” compared with {{target}}",
          // No second side: formulas worked out from the query's own columns.
          ownColumns: "Calculated columns on “{{base}}”",
          targets: {
            previous_period: "the previous period",
            previous_year: "the same period last year",
            query: "“{{name}}”",
            self: "its own columns",
          },
          unnamedQuery: "another saved query",
          periods: "{{current}}, compared with {{previous}}",
          matchOn: "Rows matched on {{columns}}",
          columnPair: "{{base}} ↔ {{compare}}",
          matchByPosition: "Rows paired by position",
          joins: {
            left: "Keeps every row of the current side",
            inner: "Keeps only rows found on both sides",
            full: "Keeps rows from either side",
          },
          matched: "{{matched}} of {{current}} rows found a match",
          noneMatched: "No rows matched, so the compared columns are empty",
          positionalMismatch:
            "Paired by position, but the current side has {{current}} rows and the compared side {{compare}}",
          formulasHeading: "Formula columns",
          formula: "{{name}} = {{expression}}",
          savedOnApply: "This comparison is saved as a new query when you apply.",
        },
      },
      errors: {
        turnFailed: "Could not handle that message. Please try again.",
        applyFailed: "Could not apply that change. Please try again.",
        previewFailed: "Could not load the preview.",
      },
    },
  },
  newAppModal: {
    title: "New App",
    duplicateTitle: "Duplicate App",
    importTitle: "Import App",
    importValidFile: "“{{fileName}}” is a valid app export. Check the details below, then import it.",
    copyName: "{{name}}-copy",
    domainLockedHint: "A copy stays in the same domain as the original app.",
    closeAria: "Close dialog",
    privateNotice:
      "This app will be visible only to you unless you share it with others.",
    fields: {
      name: "Name",
      description: "Description",
      category: "Category",
      createCategory: "Create new category…",
      newCategory: "New Category Name",
      domain: "Domain",
    },
    actions: {
      cancel: "Cancel",
      addApp: "Add App",
      duplicateApp: "Duplicate App",
      importApp: "Import App",
    },
    validation: {
      required: "This field is required.",
    },
    errors: {
      saveFailed: "Could not save the app. Please try again.",
    },
  },
  addToApp: {
    appLabel: "App",
    tabLabel: "Tab",
    selectTabHint: "Select the tab in this app to add to.",
    loadingTabs: "Loading tabs…",
    noApps: "No apps yet. Create an app in App Studio first.",
  },
  addChart: {
    title: "Add chart to app",
    closeAria: "Close dialog",
    description:
      'Add "{{chartTitle}}" to an existing app. The chart will refresh with live data each time you open the app.',
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for this chart.",
    errors: {
      saveFailed: "Could not add the chart to the app. Please try again.",
    },
  },
  deleteChart: {
    deleteAria: "Delete chart",
    confirmTitle: "Delete chart?",
    confirmMessage:
      "This chart will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the chart. Please try again.",
    },
  },
  editChart: {
    openAria: "Edit chart",
  },
  // Hover on a cut-off table cell. It says what clicking does rather than repeating the value:
  // the click opens the whole thing in a panel that wraps, scrolls and can be selected, so a
  // tooltip carrying the same text was the same content twice, in the worse of the two places.
  tableCell: {
    clippedHint: "Click to see the full value",
  },
  addTable: {
    title: "Add table to app",
    closeAria: "Close dialog",
    description:
      "Add this table to an existing app. The table will refresh with live data each time you open the app.",
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for this table.",
    fields: {
      description: "Description (optional)",
      descriptionPlaceholder: "Brief description of what this table shows…",
    },
    errors: {
      saveFailed: "Could not add the table to the app. Please try again.",
    },
  },
  addCards: {
    title: "Add cards to app",
    closeAria: "Close dialog",
    description:
      "Add cards from this answer to an existing app as KPI cards. They will refresh with live data each time you open the app.",
    cardsLabel: "Cards to add",
    cancel: "Cancel",
    confirm: "Add to app",
    adding: "Adding…",
    noSql: "No SQL query is available for these cards.",
    errors: {
      cardRequired: "Select at least one card to add.",
      saveFailed: "Could not add the cards to the app. Please try again.",
    },
  },
  deleteTable: {
    deleteAria: "Delete table",
    confirmTitle: "Delete table?",
    confirmMessage:
      "This table will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the table. Please try again.",
    },
  },
  editTable: {
    openAria: "Edit table",
  },
  addAppKpi: {
    formatOptions: {
      number: "Number",
      percent: "Percent",
      currency: "Currency",
      text: "Text",
    },
    valueColumn: {
      auto: "Auto (single column)",
    },
  },
  deleteKpi: {
    deleteAria: "Delete KPI card",
    confirmTitle: "Delete KPI card?",
    confirmMessage:
      "This KPI card will be permanently removed from the app. This action cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    deleting: "Deleting…",
    errors: {
      deleteFailed: "Could not delete the KPI card. Please try again.",
    },
  },
  editKpi: {
    openAria: "Edit KPI card",
  },
  addNuggetFeed: {
    title: "Add Nugget Feed",
    closeAria: "Close dialog",
    description:
      "Pick a timeframe, then choose which of the matching nuggets to show in this widget.",
    fields: {
      timeframe: "Timeframe",
    },
    preview: {
      legend: "Nuggets",
      loading: "Loading nuggets…",
      empty: "No nuggets were created in this timeframe.",
      searchPlaceholder: "Search nuggets…",
      noSearchMatches: "No nuggets match your search.",
      countLabel_one: "{{count}} nugget in this timeframe",
      countLabel_other: "{{count}} nuggets in this timeframe",
      selectedLabel: "{{count}} selected",
      selectAll: "Select all",
      clearAll: "Clear",
      listAriaLabel: "Nuggets in the selected timeframe",
      retry: "Retry",
      errors: {
        loadFailed:
          "Could not load nuggets for this timeframe. Please try again.",
      },
    },
    cancel: "Cancel",
    confirm: "Add",
    adding: "Adding…",
    errors: {
      saveFailed: "Could not add the Nugget Feed. Please try again.",
    },
  },
  editNuggetFeed: {
    title: "Edit Nugget Feed",
    closeAria: "Close dialog",
    description: "Change the timeframe or tab for this Nugget Feed.",
    resnapshotNotice:
      "Changing the timeframe re-matches nuggets now — any nuggets you removed from this feed will be re-added if they still fall in the new window.",
    cancel: "Cancel",
    confirm: "Save",
    saving: "Saving…",
    errors: {
      saveFailed: "Could not save the Nugget Feed. Please try again.",
    },
  },
  nuggetFeed: {
    timeframeOptions: {
      last_1_day: "Last 1 day",
      last_7_days: "Last 7 days",
      last_1_month: "Past 1 month",
      last_calendar_week: "Last calendar week",
      last_calendar_month: "Last calendar month",
    },
    empty: "No nuggets matched this timeframe.",
    listAriaLabel: "Nugget feed",
    editAria: "Edit Nugget Feed",
    deleteAria: "Delete Nugget Feed",
    deleteConfirmTitle: "Delete Nugget Feed?",
    deleteConfirmMessage:
      "This Nugget Feed widget will be permanently removed from the app. The nuggets themselves are not affected. This action cannot be undone.",
    deleteCancel: "Cancel",
    deleteConfirm: "Delete",
    errors: {
      updateFailed: "Could not update the Nugget Feed. Please try again.",
      deleteFailed: "Could not delete the Nugget Feed. Please try again.",
      removeNuggetFailed:
        "Could not remove the nugget from this feed. Please try again.",
    },
  },
  addTextWidget: {
    title: "Add text",
    closeAria: "Close dialog",
    description:
      "Write a note, an explanation, or a short summary for this app.",
    cancel: "Cancel",
    confirm: "Add",
    adding: "Adding…",
    errors: {
      saveFailed: "Could not add the text widget. Please try again.",
    },
  },
  editTextWidget: {
    title: "Edit text",
    closeAria: "Close dialog",
    description: "Change the heading, the text, or where this widget sits.",
    cancel: "Cancel",
    confirm: "Save",
    saving: "Saving…",
    errors: {
      saveFailed: "Could not save the text widget. Please try again.",
    },
  },
  textWidget: {
    fields: {
      heading: "Heading (optional)",
      content: "Text",
      // The whole authoring model, in one line. There is no markup to learn: any other line is a
      // paragraph, and line breaks are kept as typed.
      contentHelp: "Start a line with - for a bullet point.",
    },
    empty: "This text widget is empty.",
    editAria: "Edit text widget",
    deleteAria: "Delete text widget",
    deleteConfirmTitle: "Delete text widget?",
    deleteConfirmMessage:
      "This text widget and its content will be permanently removed from the app. This action cannot be undone.",
    deleteCancel: "Cancel",
    deleteConfirm: "Delete",
    errors: {
      updateFailed: "Could not update the text widget. Please try again.",
      deleteFailed: "Could not delete the text widget. Please try again.",
    },
  },
  tabs: {
    selectLabel: "Tab",
    deleteAria: "Delete tab {{name}}",
    errors: {
      tabRequired: "Select a tab for this widget.",
      renameFailed: "Could not rename the tab. Please try again.",
      reorderFailed: "Could not save the new tab order. Please try again.",
      deleteFailed: "Could not delete the tab. Please try again.",
    },
  },
  addAppTab: {
    title: "Add tab",
    closeAria: "Close dialog",
    description:
      "Name the tab. Creating the first tab moves all existing widgets into it; new widgets are then added to a tab you choose.",
    fields: {
      name: "Tab name",
    },
    cancel: "Cancel",
    save: "Add tab",
    saving: "Saving…",
    errors: {
      nameRequired: "Tab name is required.",
      saveFailed: "Could not add the tab. Please try again.",
    },
  },
  snapshots: {
    menuLabel: "Snapshots",
    rename: "Rename",
    delete: "Delete",
    empty: "This app has no snapshots yet.",
    disabledUnpublishedTooltip: "Snapshots can be managed only after the app is published",
  },
  deleteAppSnapshot: {
    title: "Delete snapshot",
    closeAria: "Close dialog",
    description: "Choose a snapshot to delete.",
    chooseAria: "Choose a snapshot to delete",
    warningPinned_one:
      "{{count}} active share link shows only this snapshot. It will stop working.",
    warningPinned_other:
      "{{count}} active share links show only this snapshot. They will stop working.",
    irreversible:
      "Deleting a snapshot permanently removes its captured data. This cannot be undone.",
    cancel: "Cancel",
    confirm: "Delete",
    errors: {
      deleteFailed: "Could not delete the snapshot. Please try again.",
    },
  },
  renameAppSnapshot: {
    title: "Rename snapshot",
    closeAria: "Close dialog",
    description: "Choose a snapshot to rename.",
    chooseAria: "Choose a snapshot to rename",
    nameLabel: "Snapshot name",
    cancel: "Cancel",
    confirm: "Rename",
    errors: {
      renameFailed: "Could not rename the snapshot. Please try again.",
    },
  },
  captureSnapshot: {
    // The toolbar button is now `snapshots.menuLabel`; this is the menu item under it.
    menu: "Capture new",
    title: "Capture snapshot",
    closeAria: "Close dialog",
    description:
      "Save this app exactly as it looks in the published app view. The snapshot keeps its own copy of every widget's data, so later edits to the app never change it.",
    fields: {
      name: "Snapshot name",
    },
    runningHint:
      "Running every widget's query. This can take up to a minute — keep this open.",
    cancel: "Cancel",
    save: "Capture",
    saving: "Capturing…",
    errors: {
      nameRequired: "Snapshot name is required.",
      saveFailed: "Could not capture the snapshot. Please try again.",
    },
  },
  deleteAppTab: {
    confirmTitle: 'Delete tab "{{name}}"?',
    confirmMessageWithFallback:
      "Choose where this tab's widgets should move. The tab will then be permanently deleted.",
    confirmMessageLastTab:
      "This is the last tab. Deleting it keeps all widgets and returns the app to its no-tabs layout.",
    fallbackLabel: "Move widgets to",
    cancel: "Cancel",
    confirm: "Delete tab",
  },
  savedQuery: {
    toggle: "Use a saved query",
    selectLabel: "Saved query",
    referenceCount: "Referenced by {{count}} widget(s)",
    empty: "No saved queries yet. Author a query below or save one first.",
    errors: {
      loadFailed: "Could not load saved queries. Please try again.",
    },
  },
  // A widget source that joins a saved query with its previous period, or with another query.
  comparison: {
    heading: "Comparison",
    help: "Compare a saved query with the same query for the previous period, the same period last year, or another saved query. Rows are matched, and formula columns are calculated from both sides. The previous period follows the tab's date filter.",
    compare: "Compare",
    compareHint:
      "Compare this query with its previous period, the same period last year, or another saved query, and add formula columns calculated from both. It starts with the previous period and a % change for each measure. Nothing is saved until you save.",
    // The query the comparison is built on: fixed, shown read-only.
    currentQuery: {
      showSql: "Show SQL",
      hideSql: "Hide SQL",
      sqlLabel: "Current query SQL",
      loading: "Loading the query…",
      remove: "Remove comparison",
      removeHint: "Go back to this query on its own. Nothing changes until you save.",
    },
    // "Remove comparison" on a widget saved on a comparison: what Save will do, and the way back.
    removal: {
      movesBack: "On save this widget goes back to “{{name}}”.",
      dropped: "Dropped, because only the comparison returns them: {{columns}}.",
      // Named by each click action's clickable column.
      droppedClickActions_one: "The click action on {{columns}} is removed with them.",
      droppedClickActions_other: "The click actions on {{columns}} are removed with them.",
      deleted: "The comparison “{{name}}” is deleted: nothing else uses it.",
      kept_one: "The comparison “{{name}}” stays saved: {{count}} other widget uses it.",
      kept_other: "The comparison “{{name}}” stays saved: {{count}} other widgets use it.",
      keptCompared: "The comparison “{{name}}” stays saved: another comparison uses it.",
      keep: "Keep comparison",
    },
    // The statements a comparison preview ran, one per side.
    executed: {
      heading: "Executed queries",
      help: "The SQL each side ran for this preview, after the tab's filters — the previous period's dates already moved back.",
      empty: "Run the comparison to see the SQL each side executes.",
      currentPeriod: "Current period",
      previous_period: "Previous period",
      previous_year: "Same period last year",
      withPeriod: "{{label}} · {{period}}",
      currentQuery: "Current query · {{name}}",
      comparedQuery: "Compared query · {{name}}",
    },
    fields: {
      base: "Current query",
      compareWith: "Compare with",
      compareQuery: "Compared query",
      join: "Rows to keep",
    },
    compareKinds: {
      previous_period: "Previous period",
      previous_year: "Same period last year",
      query: "Another saved query",
      self: "Columns of this query",
    },
    // "Columns of this query": no second side, formulas over the query's own columns.
    ownColumns: {
      intro: "Each row of this query gets the formula columns below, worked out from its own columns.",
    },
    joins: {
      left: "Every row of the current query",
      inner: "Only rows found on both sides",
      full: "Rows from either side",
    },
    periods: "This period: {{current}} · Compared with: {{previous}}",
    match: {
      heading: "Match rows on",
      help: "Rows of the two sides are paired where these columns hold the same value, such as a date or a region. Leave it empty to pair rows by position, which suits a single total.",
      positional: "No match columns: rows are paired by position, first with first.",
      currentColumn: "Current column",
      compareColumn: "Compared column",
      add: "Add match column",
      removeAria: "Remove match column",
    },
    formulas: {
      heading: "Formula columns",
      help: "Columns calculated for each row. Name a column of the current side directly (revenue) or as current.<column>, and the other side as previous.<column> (or compare.<column>). Allowed: numbers, 'text', TRUE, FALSE, NULL, + - * / %, = <> < <= > >=, IS [NOT] NULL, AND, OR, NOT, CASE WHEN … THEN … ELSE … END, IF, NULLIF, COALESCE, ABS, ROUND, GREATEST and LEAST. Two plain numbers compare as numbers; anything else, such as a phone number, as text.",
      helpOwn: "Columns calculated for each row from this query's own columns: name them directly (call_number). Allowed: numbers, 'text', TRUE, FALSE, NULL, + - * / %, = <> < <= > >=, IS [NOT] NULL, AND, OR, NOT, CASE WHEN … THEN … ELSE … END, IF, NULLIF, COALESCE, ABS, ROUND, GREATEST and LEAST. Two plain numbers compare as numbers; anything else, such as a phone number, as text.",
      empty: "No formula columns yet.",
      name: "Column name",
      expression: "Formula",
      expressionHint: "For example (current.revenue - previous.revenue) / NULLIF(previous.revenue, 0) * 100",
      expressionHintOwn: "For example CASE WHEN call_number = call_number_previous_period THEN 'Same' ELSE 'Different' END",
      insertColumn: "Insert column",
      add: "Add formula column",
      removeAria: "Remove formula column",
    },
    warnings: {
      noMatches:
        "No rows matched on {{columns}}. Match on a column whose values repeat in both periods, such as a date, or remove the match columns to pair rows by position.",
      positionalMismatch:
        "Rows are paired by position, but the current side has {{current}} rows and the other side has {{compare}}. Match on a column such as the date so rows pair by value.",
    },
    errors: {
      baseRequired: "Select the query to compare.",
      compareQueryRequired: "Select the query to compare with.",
      sideIsSelf: "A comparison can't compare itself.",
      ownColumnsFormulaRequired: "Add at least one formula column to work out from this query's columns.",
      tooManyMatchColumns: "Match on at most {{max}} columns.",
      matchColumnIncomplete: "Pick both columns of every match row, or remove it.",
      tooManyFormulas: "Add at most {{max}} formula columns.",
      formulaNameRequired: "Give every formula column a name.",
      formulaNameInvalid:
        "“{{name}}” isn't a valid column name. Use letters, digits and underscores, starting with a letter.",
      formulaNameDuplicate: "There are two formula columns named “{{name}}”.",
      formulaExpressionRequired: "Write a formula for “{{name}}”.",
      formulaExpressionTooLong: "The formula for “{{name}}” is longer than {{max}} characters.",
      columnsLoadFailed: "Could not load this query's columns.",
    },
    queryName: {
      previous_period: "{{name}} vs previous period",
      previous_year: "{{name}} vs same period last year",
      query: "{{name}} vs {{compared}}",
      self: "{{name}} with calculated columns",
    },
    // Table columns a comparison adds beside one of the widget's own.
    columnLabels: {
      previous: "{{label}} (previous)",
      compared: "{{label}} (compared)",
      changePercent: "{{label}} change %",
    },
    // Why "Compare" or a setting is unavailable on this widget.
    blocked: {
      fileBacked: "Files can't be compared. Comparisons work on SQL queries.",
      alreadyComparison: "This widget already shows a comparison.",
      breakdown: "This widget has a breakdown. Remove it to compare periods.",
      unsavedQuery: "Save your query changes first.",
      breakdownField: "Breakdowns aren't available on a comparison.",
    },
  },
  // The two tabs splitting the add/edit widget dialogs: the query and its result, then the widgets.
  widgetDialog: {
    tabsAria: "Widget dialog sections",
    queryTab: "Query & data",
    widgetsTab: "Widget settings",
    helpAria: "More information",
    // What each widget field does, shown in the "?" beside it.
    help: {
      widgetType:
        "Chart: plots measures against a dimension.\nTable: shows the rows, with column control and optional row approval.\nKPI card: a single headline number.",
      title: {
        chart: "The chart's title in the app, shown above it. Optional.",
        table:
          "The table's title in the app, shown above it with its row count. It also names the table's exports. Optional.",
        kpi: "The label shown on the KPI card. Required.",
      },
      width:
        "How wide the widget is on the app's grid, from Extra Small to Full. Hover a block to see its size.",
      tab: "The app tab this widget appears on.",
      breakdown:
        "Let viewers switch how this widget breaks its data down — by day, week or month, by region, or any other way — without filtering out rows. Each option gets its own view of the query, written by AI or taken from a saved query. It saves from its own dialog.",
      aiSummary:
        "Shows the AI-written summary under the chart or table. Hiding it only removes it from view: it is still generated, because the tab overview is built from it. When every chart and table on a row hides its summary, the row's cards get shorter.",
      columns:
        "Choose which of the query's columns the table shows. A hidden column stays in the query, and can still be sent with approvals.\nIn the preview below: drag a column header to reorder, click its name to rename it, and click the arrow beside it to sort.",
      frozenColumns:
        "How many columns, from the left, stay in view while the table scrolls sideways, so every row stays identifiable. 0 freezes none. On a rotated table, 1 keeps the column naming each row in view.",
      pinnedRows:
        "Rows such as totals that stay in view above or below the table while it scrolls, on every page. They are not one of the 10 rows a page shows, and are never sorted with the rows.\nEach row's calculation runs under every column without a cell of its own, over every row of the result rather than the page on screen. Sum and Average fill number columns only, and use the column's format; Count counts non-empty values.\nAdd a column cell to show something else under one column: another calculation, a calculation over a different column (a hidden one too), or fixed text.\nOn a rotated table, a pinned row either calculates down the rotated column above it, or keeps one column's own row in view, like a second header: choose \"A column's row\" under Shows. That row leaves the rows that scroll, and stays put on every page; freeze 1 column to keep its name in view while scrolling sideways too.",
      rotate:
        "Draw the table on its side: each row becomes a column, every shown column becomes a row, and the first column lists their names.\nPick a column whose values head the rotated columns, or choose own headings when the data has none. Either way, type over any heading in the rotated preview to rename it.\nColumn order and names still apply, and the sort now orders the rotated table's columns. Not available on a table with row approval.",
      format:
        "Number: add a symbol before or after the value ($ 1,650 or 45 %), group thousands with commas and fix the decimals. The symbol is only added — 45 with % shows as 45 %, not 4500 %.\nDate & time: choose how the incoming value is read (leave it blank for date and timestamp columns) and how it is shown. Dates show as the data has them, in no particular time zone; Unix time shows in UTC.\nA value the format cannot read, such as text under a number format, shows as it is.",
    },
    // The Format section: how each column's values are displayed, per widget.
    format: {
      intro:
        "Choose how each column's values are shown. Display only: the query, sorting, filters and drill-downs keep the raw values. Exports show the values in this format, as the table does.",
      empty: "Run the query so its columns are known, then format them.",
      fieldAria: "Format for {{column}}",
      roles: {
        dimension: "Axis",
        measure: "Series",
        stack: "Legend",
      },
      typeLabel: "Format",
      types: {
        none: "None",
        number: "Number",
        date: "Date & time",
      },
      symbolLabel: "Symbol",
      symbolNone: "None",
      positionLabel: "Symbol side",
      positions: {
        prefix: "Before ({{example}})",
        suffix: "After ({{example}})",
      },
      decimalPlacesLabel: "Decimals",
      decimalPlacesAuto: "As is",
      thousandsSeparator: "Thousands separator (1,650)",
      inputPatternLabel: "Read values as",
      inputPatternPlaceholder: "Automatic (ISO date or timestamp)",
      inputPatternHelp:
        "How incoming text is written, e.g. DD/MM/YYYY. X reads Unix seconds, x milliseconds.",
      outputPatternLabel: "Show as",
      outputPatternHelp:
        "YYYY year, MMM or MM month, DD day, ddd weekday, HH or hh hour, mm minute, ss second, A AM/PM. Wrap other letters in [ ].",
      invalidPattern: "Use at least one token such as YYYY, MM or DD. Not saved until it has one.",
      example: "Example: {{raw}} → {{formatted}}",
      exampleUnreadable: "Example: {{raw}} cannot be read with this format, so it shows as is.",
      noSample: "Run the query to see an example.",
    },
  },
  editWidget: {
    title: "Edit widget(s)",
    closeAria: "Close dialog",
    // Before the source section's heading (Query, Form, …): Edit keeps the saved source.
    builtWith: "Built with",
    // Super Administrators only: the statement the grid actually runs, for debugging filters.
    executedSql: {
      heading: "Executed query (with filters)",
      help: "The final SQL this widget runs on the grid, with the tab's currently applied filters — or its saved defaults when none are applied. Built from the saved query, so unsaved edits above are not included. Visible to Super Administrators only.",
      sqlLabel: "Executed SQL",
      loading: "Building the executed query…",
      loadFailed: "Could not build the executed query.",
      noSql: "This widget reads files, so it runs no SQL.",
      rejected: "The domain check refuses this query: {{reason}}",
      templateUsed: "A stored filter template built part of this query.",
      notFilteredBy: "Not filtered by: {{names}}",
    },
    // Pills inside a widget card. They name the thing being configured, not the act of
    // configuring it, so the bar reads as a set of parts rather than a set of screens.
    sections: {
      aria: "Widget settings sections",
      columns: "Columns",
      pinnedRows: "Pinned rows",
      drilldowns: "On click",
      approvals: "Approvals",
      chart: "Chart",
      format: "Format",
      value: "Value",
      drilldown: "On click",
      style: "Style",
      preview: "Preview",
      advanced: "Raw settings",
    },
    // A KPI card's Style section: base colours, font sizes, value-based colour rules, change chip.
    style: {
      cardHeading: "Card",
      cardColor: "Card color",
      cardColorPicker: "Pick the card color",
      textColor: "Text color",
      textColorPicker: "Pick the text color",
      defaultColorHint: "Leave empty for the default.",
      valueFontSize: "Value size",
      labelFontSize: "Name size",
      fontSizes: {
        sm: "Small",
        md: "Medium (default)",
        lg: "Large",
        xl: "Extra large",
      },
      rulesHeading: "Color rules",
      rulesDescription:
        "Change the card's colors by its value. Rules are checked from the top, and the first one that matches is used.",
      rulesTextFormat: "Color rules only apply to numbers. This card uses the Text format.",
      operator: "When the value is",
      operators: {
        gt: "Greater than",
        gte: "Greater than or equal to",
        lt: "Less than",
        lte: "Less than or equal to",
        eq: "Equal to",
        between: "Between",
      },
      value: "Value",
      from: "From",
      to: "To",
      addRule: "Add rule",
      ruleAria: "Color rule {{position}}",
      ruleCardColorAria: "Rule {{position}} card color",
      ruleCardColorPicker: "Pick rule {{position}} card color",
      ruleTextColorAria: "Rule {{position}} text color",
      ruleTextColorPicker: "Pick rule {{position}} text color",
      moveUp: "Move up",
      moveUpAria: "Move rule {{position}} up",
      moveDown: "Move down",
      moveDownAria: "Move rule {{position}} down",
      removeRule: "Remove rule",
      removeRuleAria: "Remove rule {{position}}",
      errors: {
        number: "Enter a number.",
        range: "Must be at least the From value.",
        color: "Pick a card color, a text color, or both.",
      },
      // The optional up/down chip under the card's name.
      change: {
        heading: "Change chip",
        description:
          "Show how the value moved, like +9.50%, using another column of this card's query. Leave the column on None for no chip.",
        textFormat: "The change chip only shows on numbers. This card uses the Text format.",
        column: "Change column",
        none: "None",
        kind: "The column holds",
        kinds: {
          percent: "Percent change",
          amount: "Change amount",
          previous: "Previous value",
        },
        label: "Text after the number",
        labelPlaceholder: "vs last week",
        size: "Chip size",
        increaseColor: "Increase color",
        increaseColorHint: "Leave empty for green.",
        increaseColorPicker: "Pick the chip's color for an increase",
        decreaseColor: "Decrease color",
        decreaseColorHint: "Leave empty for red.",
        decreaseColorPicker: "Pick the chip's color for a decrease",
      },
    },
    query: {
      // Shown in the "?" beside Results.
      description:
        'The query behind this widget and the rows it returns. Click Refresh to rerun it after a change. Other widgets using the same query change too; they appear under "Linked widgets" in Widget settings once you edit it.',
      loading: "Loading query…",
    },
    linked: {
      heading: "Linked widgets",
      description:
        "These widgets also reuse this query, so your query changes affect them too. Adjust them here before saving.",
    },
    added: {
      heading: "New widgets",
      description:
        "These widgets are added to the app when you save, reading from the same query as the widget above.",
      addWidget: "Add another widget",
      kindLabel: "Widget type",
      removeAria: "Remove this widget",
    },
    drilldown: {
      // Shared by the card and table sections so the two read as one feature: one `heading`, one
      // `hint`, one `targetLabel`, and descriptions that differ only in what is clicked. Keep them
      // as single keys rather than a per-section copy — a duplicate is how the two drifted apart
      // before (the table section used to say "Drill-downs" and never mentioned the hiding).
      heading: "On click",
      description: "Clicking this card opens the chosen widget.",
      enable: "Make this card clickable",
      hint: "The chosen widget is hidden from the tab and opens in a dialog instead.",
      noCandidates:
        "Add a chart or table to this tab first, then pick it here.",
      targetLabel: "Opens widget",
      tabLocked: "Moves with the widget that opens it",
      untitledChart: "Untitled chart",
      untitledTable: "Untitled table",
      table: {
        description:
          "Clicking a cell opens the chosen widget, filtered by that row.",
        add: "Add a click action",
        removeAria: "Remove this click action",
        triggerLabel: "Clickable column",
        valueLabel: "Filter by columns",
        valueHint:
          "The clicked row's values in these columns filter the widget. Pick more than one to narrow it further.",
        empty: "No click actions yet.",
        noColumns:
          "Run this table once so its columns are known, then add a click action.",
        // Shown appended to an already-taken column in the picker, so it reads
        // "Students_Pass — Already used" and needs no noun of its own.
        columnTaken: "Already used",
        noSharedColumn:
          "This table and the chosen widget report no column in common, so there is nothing to filter by. Pick a different widget.",
        incomplete:
          "Finish every click action before saving: each one needs a widget, a clickable column and at least one filter column.",
        presentationLabel: "Opens as",
        presentationDialog: "Full screen",
        presentationDrawer: "Side panel",
        presentationDialogHint:
          "The opened widget covers the page until it is closed.",
        presentationDrawerHint:
          "The opened widget slides in from the right, leaving this table visible beside it.",
        addLink: "Add link",
        removeLinkAria: "Remove this link",
        linkColumnLabel: "Link column",
        linkColumnHint: "Each row's value in this column is the URL the link opens.",
        linkTextLabel: "Link text",
        linkTextPlaceholder: "Click here to view the audio file",
        linkTextHint: "Every row shows this text. Clicking it opens the URL in a new tab.",
        linkIncomplete: "Give every link some text before saving.",
      },
    },
    json: {
      apply: "Apply",
      heading: "Settings as JSON",
      errors: {
        invalidJson: "Must be valid JSON.",
        invalidChart:
          "Chart definition needs a string id and recommended_chart_type.",
        invalidDisplayConfig:
          "Display config must have a columns array of objects with a key.",
        invalidKpiConfig:
          "KPI config must have a format of number, percent, currency, or text.",
      },
    },
    actions: {
      cancel: "Cancel",
      save: "Save changes",
      saving: "Saving…",
    },
    errors: {
      noQuery:
        "No query is associated with this widget, so it can't be edited here.",
      queryLoadFailed: "Could not load the query. Please try again.",
      saveFailed: "Could not save your changes. Please try again.",
    },
  },
  addWidgets: {
    title: "Add widgets",
    closeAria: "Close dialog",
    query: {
      // Shown in the "?" beside "Build widget with": what each option is, so the choice is clear.
      description:
        'Choose where your widgets get their data:\n• JSON: pick JSON files from storage. Tables only.\n• Query: write the SQL yourself.\n• Prompt: describe what you want in plain words; AI writes the query and suggests widgets.\n• Form: pick metrics, dimensions and a time range; the SQL is written for you.\n• Saved query: reuse a query this app already has, then pick it below.\n• Comparison: compare a saved query with its previous period or another saved query, and add calculated columns.',
      sectionHeading: "Query",
      queryTabDescription:
        "Write a SQL query, then click Run query to see its rows. Your widgets are built from the columns it returns.",
      fields: {
        sql: "SQL query",
      },
      inputMode: {
        label: "Build widget with",
        query: "Query",
        form: "Form",
        prompt: "Prompt",
        s3: "JSON",
        savedQuery: "Saved query",
        comparison: "Comparison",
      },
      s3: {
        title: "Select files",
        help: "Pick a folder to include every JSON file inside it, now and later, or pick individual files.",
        loading: "Loading files…",
        loadFailed: "Could not load files.",
        empty: "No JSON files found for this domain.",
        emptyHint: "Upload files under {{prefix}} to use them here.",
        includedByFolder: "Included by the selected folder",
        selectedSummary: "{{count}} selected",
        selectionRequired: "Select at least one folder or file.",
        tableOnly: "Files can only be used by a table widget.",
        expand: "Expand {{name}}",
        collapse: "Collapse {{name}}",
      },
      errors: {
        sqlRequired: "SQL query is required.",
        queryRequired: "Select a saved query.",
      },
    },
    results: {
      heading: "Results",
      sqlLabel: "Query SQL",
      run: "Run query",
      refresh: "Refresh",
      loading: "Running query…",
      empty: "The query returned no rows.",
      errors: {
        failed: "Could not run the query. Please try again.",
      },
    },
    widgets: {
      heading: "Widgets",
      hint: "Provide and run a query above, then compose widgets from its result.",
    },
    compose: {
      // Shown in the "?" beside the Widgets heading.
      description:
        "Add one or more widgets. Each is built from the query's result, and its preview updates as you change it.",
      addWidget: "Add another widget",
      removeAria: "Remove widget",
      kindLabel: "Widget type",
      kind: {
        chart: "Chart",
        table: "Table",
        kpi: "KPI card",
      },
      // One Title field on every widget card: a chart's title, a table's description (shown as
      // its title in the app) and a KPI's name, which cannot be blank.
      titleLabel: "Title (optional)",
      titleRequiredLabel: "Title",
      titlePlaceholder: {
        chart: "e.g. Revenue by month",
        table: "e.g. Open cases",
        kpi: "e.g. Total revenue",
      },
      kpiFormatLabel: "Format",
      kpiValueColumnLabel: "Value column",
      // Shown with the KPI's title in the info tooltip on the card; blank means no info icon.
      kpiDescriptionLabel: "Description (optional)",
      aiSummaryLabel: "AI summary",
      aiSummaryToggle: "Show under the widget",
      kpiDescriptionPlaceholder: "e.g. Revenue across every store in the selected period",
      kpiPreviewName: "KPI preview",
      columnSpanLabel: "Width",
      table: {
        columnsLabel: "Columns",
        columnsSelected: "{{count}} of {{total}} shown",
        hint: "Drag column headers to reorder, rename inline, and click the arrow to sort.",
        columnNameLabel: "Column display name",
        dragAria: "Reorder column",
        sortAria: "Toggle sort",
        empty: "No columns selected.",
        rotate: {
          label: "Rotate",
          off: "Not rotated",
          ownHeadings: "Rotated, own headings",
          fromColumn: "Headings from a column",
          selected: "Headers from {{column}}",
          idLabel: "First column header",
          idPlaceholder: "Heading",
          headingAria: "Heading for column {{number}}",
          approvalDisabled: "Not available while row approval is on.",
          previewLabel: "Rotated preview. Type over any heading to rename it.",
        },
        frozenColumns: "Freeze columns",
        pinnedRows: {
          intro:
            "Rows such as totals that stay in view above or below the table on every page, calculated over the whole result.",
          rotatedNote:
            "This table is rotated, so a pinned row either calculates down the rotated column above it or keeps one column's own row in view, like a second header. Column cells don't apply while it's rotated.",
          rowHeading: "Pinned row {{number}}",
          removeRow: "Remove pinned row {{number}}",
          addRow: "Add pinned row",
          maxRows: "A table can pin up to {{count}} rows.",
          defaultLabel: "Total",
          label: "Label",
          labelHelper: "Shown in the first column.",
          position: "Position",
          positions: {
            top: "Top, under the header",
            bottom: "Bottom",
          },
          aggregate: "Calculation",
          aggregateHelper: "Under every column without its own cell.",
          aggregateHelperRotated:
            "Down each column. Sum and Average add only the numbers, skipping text; a column with no numbers stays empty.",
          noAggregate: "None",
          aggregates: {
            sum: "Sum",
            avg: "Average",
            min: "Minimum",
            max: "Maximum",
            count: "Count",
            count_distinct: "Count distinct",
          },
          cellsHelper:
            "Column cells show something else under one column: another calculation, a calculation over a different column, or fixed text.",
          addCell: "Add column cell",
          removeCell: "Remove the cell under {{column}}",
          cellColumn: "Column",
          cellType: "Shows",
          cellTypes: {
            aggregate: "Calculation",
            text: "Fixed text",
          },
          cellAggregate: "Calculation",
          cellSource: "Of column",
          cellSourceSame: "This column",
          cellText: "Text",
          cellTextPlaceholder: "Blank",
          cellAria: "Cell under {{column}}",
          hiddenColumn: "{{column}} (hidden)",
          rowAggregate: "Row calculation",
          rowShows: "Shows",
          rowShowsOptions: {
            aggregate: "A calculation",
            column: "A column's row",
          },
          rowColumn: "Column",
          rowColumnHelper: "Its row stays in view instead of scrolling with the others.",
          columnRowWhenRotated:
            "Shows {{column}}'s row while the table is rotated. Drawn the usual way round, it shows its label and calculation.",
          previewLabel:
            "Preview, calculated over the preview's rows. In the app, pinned rows calculate over every row of the result.",
        },
        approvalLabel: "Row approval",
        approvalHelper:
          "Lets viewers approve rows in the published app. Each approval saves a file with the columns selected below.",
        approvalColumnsLabel: "Approval columns",
        approvalColumnsSelected: "{{count}} of {{total}} selected",
        approvalColumnsHidden: "Hidden",
        approvalColumnsHelper:
          "Saved to the approval file. Any query column can be included, even one the table does not show. The shown ones identify the row, so include a unique column, or matching rows are approved together.",
        approvalSourcesLabel: "Also hide rows approved on",
        approvalSourcesSelected: "{{count}} selected",
        approvalSourcesHelper:
          "Approvals are recorded per widget, so a row approved on another table stays here until you name it. This is one-way — to hide rows both ways, set it on that table too.",
        approvalSourcesUncovered:
          "This table does not show the columns {{tables}} identifies rows by, so those approvals will not hide anything here.",
        rowKeyLabel: "Row key",
        rowKeyInferred: "Worked out from the columns",
        rowKeyHelperInferred:
          "Rows are told apart by every approval column except the ones viewers change. Pick a key column instead if the other values can change after a row is approved, or the same record is approved on another table.",
        rowKeyHelperNamed:
          "Only these columns decide whether two rows are the same row. Anything else can change without bringing an approved row back.",
        rowKeyErrors: {
          keyColumnNotApproved:
            "A row key column has to be selected for approval, or it is never written out with the row.",
          keyColumnIsLevel:
            "A classification column cannot be a row key: viewers change it before approving, so a corrected row would stop matching.",
        },
        taxonomy: {
          enableLabel: "Classification",
          enableHelper:
            "Lets viewers correct classification columns against a controlled list before approving a row.",
          sourceTitle: "Classification files",
          sourceHelper:
            "Pick the JSON files holding the classification list. Each file is an array of objects.",
          hierarchicalLabel: "Cascading levels",
          hierarchicalHelper:
            "Each level is filtered by the one above it, and changing a level clears the ones below it.",
          hierarchicalBlocked:
            "Linked levels need exactly one file, since the relationship is defined inside a single document.",
          independentHelper:
            "Each column offers all the values of its property, independently of the others.",
          propertyLabel: "Level {{level}} property",
          columnLabel: "Level {{level}} column",
          flatPropertyLabel: "Property {{level}}",
          flatColumnLabel: "Column {{level}}",
          addLevel: "Add level",
          removeLevel: "Remove level {{level}}",
          propertyLoading: "Reading the classification files…",
          propertyLoadFailed:
            "Could not read those files. Type the property names instead.",
          propertyEmpty: "Those files hold no object properties.",
          propertyFreeTextHint:
            "Type the property name exactly as it appears in the files.",
          identityHelper:
            "Level columns are left out of a row's approval identity, since viewers may change them. Keep at least one other approval column that identifies the row.",
          errors: {
            sourceRequired: "Pick at least one classification file or folder.",
            hierarchicalNeedsSingleFile:
              "Linked levels need exactly one file selected. Pick a single file, or turn off the dependency.",
            levelsRequired: "Every level needs a property and a column.",
            duplicateProperty: "Each level needs a different property.",
            duplicateColumn: "Each level needs a different column.",
            columnNotApproved:
              "Each level column must also be selected as an approval column.",
            identityExhausted:
              "Select at least one approval column that is not a taxonomy level.",
          },
        },
      },
      chart: {
        dimensionLabel: "Dimension (category axis)",
        measuresLabel: "Measures",
        measuresSelected: "{{count}} of {{total}} shown",
        legendHint:
          "Rename each legend inline, and drag to reorder when more than one is shown. With several series, choose how each is drawn: bars or a line, its axis, and stacked bars.",
        legendNameLabel: "Legend name",
        seriesTypeLabel: "Draw as",
        seriesTypeBar: "Bar",
        seriesTypeLine: "Line",
        axisLabel: "Axis",
        axisAuto: "Auto axis",
        axisLeft: "Left axis",
        axisRight: "Right axis",
        stackLabel: "Stack",
        dragAria: "Reorder legend",
        empty: "Select at least one measure to plot.",
      },
      errors: {
        widgetRequired: "Add at least one widget.",
        kpiNameRequired: "KPI cards need a title.",
        kpiStyleInvalid: "Fix the highlighted fields in the KPI card's Style tab.",
        formatInvalid:
          "Fix the date format of {{column}} in the Format section: its pattern needs a date part such as YYYY, MM or DD.",
      },
    },
    actions: {
      cancel: "Cancel",
      save: "Add widgets",
      saving: "Adding…",
    },
    errors: {
      saveFailed: "Could not add the widgets. Please try again.",
    },
  },
  widgetAssistant: {
    heading: "Refine your widget",
    // Shown in the "?" beside the heading.
    description:
      "Ask for a change and the widgets are redesigned from the same data, e.g. \"make it a line chart\", \"only show month and revenue\" or \"give me a card per column\".\nYour query is never changed here. To change the data, edit the query and run it again.",
    placeholder: 'Describe a change, e.g. "make it a line chart"',
    sendAria: "Send prompt",
    stopAria: "Stop generating",
    transcriptAria: "Widget prompt conversation",
    generating: "Redesigning the widgets…",
    errors: {
      turnFailed: "Could not update the widgets. Please try again.",
    },
  },
  promptBuilder: {
    emptyState: {
      widgets:
        "Describe the widgets you want — charts, tables, or KPI cards. The query and widgets are generated for you, and you can refine them with follow-up prompts.",
      chart:
        "Describe the chart you want. The SQL query and chart will be generated for you, and you can refine them with follow-up prompts.",
      table:
        "Describe the table you want. The SQL query and table will be generated for you, and you can refine them with follow-up prompts.",
      kpi: "Describe the KPI you want. The query must produce a single aggregated value, and you can refine it with follow-up prompts.",
    },
    placeholder: {
      widgets:
        'Describe what to show or change, e.g. "monthly revenue by region as a bar chart" or "make it a line chart"',
      chart: "Prompt your widget — describe what to show, or ask for a change…",
      table:
        "Prompt your table — describe the data to show, or ask for a change…",
      kpi: "Prompt your KPI — describe the value to track, or ask for a change…",
    },
    sendAria: "Send prompt",
    stopAria: "Stop generating",
    transcriptAria: "Prompt conversation",
    generating: "Generating…",
    sqlHeading: "Generated SQL",
    kpiPreviewName: "KPI preview",
    resultRequired: "Generate a result with a prompt before saving.",
    warnings: {
      no_chart_generated:
        "A chart couldn't be generated for this result — showing the data instead.",
      kpi_shape_invalid:
        "The query didn't return a single value (1 row, 1 column). Refine your prompt to ask for one aggregated value.",
      no_sql_generated:
        "No SQL query was generated. Try describing the data you want to see.",
      no_widgets_generated:
        "No widgets could be generated for this result. Try rephrasing what you want to see.",
      kpi_value_column_invalid:
        "A KPI card was skipped because its value column couldn't be resolved. Name the column to display.",
    },
    errors: {
      turnFailed: "Could not generate a result. Please try again.",
    },
    discardDialog: {
      title: "Discard this draft?",
      messageResult:
        "Your generated widget and prompt history haven't been saved. If you discard now, they'll be lost — click \"Save changes\" instead to keep this widget.",
      messageGenerating:
        "A result is still being generated and hasn't been saved. If you discard now, the work in progress will be lost.",
      keepEditing: "Keep editing",
      discard: "Discard draft",
    },
  },
} as const;
